import Component from "../../lib/component";
import { getThrottledFunction } from "../../helpers/throttle";

export const PageHeadingConfig = {
  componentTypeKey: "PageHeading",
  baseHtmlClass: "page_heading",
  selector: ".page_heading",

  titleSelector: ".page_heading__title",
  actionSelector: ".page_heading__action",
  startSelector: ".page_heading__start",
  endSelector: ".page_heading__end",

  // The measured-height custom property that #page's reserved padding consumes.
  // The SCSS declares breakpoint-based fallback values for first paint / no-JS;
  // evaluate() refines them to the heading's actual rendered height.
  heightProperty: "--page-heading-height",
};

// Oscillation breaker: more than OSCILLATION_MAX_FLIPS stacked-state flips inside
// OSCILLATION_WINDOW_MS means the layout and the stacked styling disagree about
// whether the content fits — i.e. a .page_heading--stacked style is reducing
// intrinsic width, violating the invariant documented in _page_heading.scss.
// A converging system flips at most once per external change.
const OSCILLATION_WINDOW_MS = 300;
const OSCILLATION_MAX_FLIPS = 2;

export class PageHeading extends Component {
  static get config() {
    return PageHeadingConfig;
  }

  constructor(id_or_element) {
    super({
      element: id_or_element,
      config: PageHeading.config,
    });

    this.titleElement = this.element.querySelector(
      PageHeading.config.titleSelector,
    );
    this.title = !!this.titleElement ? this.titleElement.innerText : "";

    this.actionElement = this.element.querySelector(
      PageHeading.config.actionSelector,
    );

    this.startWrapElement = this.element.querySelector(
      PageHeading.config.startSelector,
    );
    this.endWrapElement = this.element.querySelector(
      PageHeading.config.endSelector,
    );

    this.isPrimary = this.getIsOnPage();

    // Stacking state bookkeeping (see evaluate()).
    this.isStacked = undefined; // unknown until the first evaluate()
    this.lastSyncedHeight = undefined;
    this.flipTimes = [];
    this.oscillationLatched = false;
    this.hasWarnedOscillation = false;

    this.warnIfStructureIsUnexpected();

    // Scroll and layout are deliberately separate paths: --scrolled is pure paint
    // (box-shadow/background) and must never feed the stacking evaluation, so
    // scrolling can never perturb the layout machine.
    const scrollHandler = getThrottledFunction(this.handleScroll.bind(this), 10);
    const resizeHandler = getThrottledFunction(this.handleResize.bind(this), 10);
    window.addEventListener("scroll", scrollHandler, true);
    window.addEventListener("resize", resizeHandler, true);
    this.onDestroy(() =>
      window.removeEventListener("scroll", scrollHandler, true),
    );
    this.onDestroy(() =>
      window.removeEventListener("resize", resizeHandler, true),
    );

    // Observes the heading's own box — fires on content/slot changes, font swaps,
    // and our own wrap transitions. An INTERNAL trigger: it re-evaluates but does
    // not clear an oscillation latch (our own writes must not un-latch us).
    // The content-sized boxes are observed as well as the heading itself —
    // __start's CHILDREN (not __start itself, which flex-grows to fill the row
    // and so barely changes) and __end (content-sized) — so content width
    // changes re-evaluate promptly even mid-transition, before the heading's
    // own height settles.
    const elementResizeHandler = getThrottledFunction(
      this.handleElementResize.bind(this),
      10,
    );
    this.observer = new ResizeObserver(elementResizeHandler);
    this.observer.observe(this.element);
    if (this.startWrapElement) {
      Array.from(this.startWrapElement.children).forEach((child) =>
        this.observer.observe(child),
      );
    }
    if (this.endWrapElement) this.observer.observe(this.endWrapElement);
    this.onDestroy(() => this.observer.disconnect());

    // Late font swaps change text widths — an external trigger.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.handleExternalChange());
    }

    this.render();
    this.evaluate();
  }

  setTitle(title) {
    this.title = title;
    this.handleExternalChange();
  }

  removeActionElement() {
    if (!this.actionElement || !this.actionElement.parentNode) {
      return;
    }
    this.actionElement.remove();
    this.handleExternalChange();
  }

  handleScroll() {
    this.isScrolled = this.getIsScrolled();
    this.render();
  }

  handleResize() {
    // Viewport resize is an external trigger: geometry genuinely changed, so a
    // previously-latched oscillation state gets a fresh chance to converge.
    this.clearOscillationLatch();
    this.isScrolled = this.getIsScrolled();
    this.render();
    this.evaluate();
  }

  handleElementResize() {
    this.evaluate();
  }

  handleExternalChange() {
    this.clearOscillationLatch();
    this.evaluate();
  }

  getHeight() {
    return this.element.offsetHeight;
  }

  getIsScrolled() {
    return window.scrollY > this.getHeight();
  }

  // True when __end has wrapped below __start — the browser's own answer to
  // "do the two sides fit beside each other?". No pixel thresholds involved.
  getIsStacked() {
    if (!this.startWrapElement || !this.endWrapElement) return false;
    return this.endWrapElement.offsetTop > this.startWrapElement.offsetTop;
  }

  // The page heading may be (or may not be) on the documents main #page element.
  // For example it might be inserted into a `Modal` which is outside of the `#page`.
  getIsOnPage() {
    // Is on the "page" if any parent within the body has the id "page".
    let parent = this.element.parentElement;
    while (parent) {
      if (parent.id === "page") return true;
      parent = parent.parentElement;
    }
    return false;
  }

  // Single writer for the stacking state and the measured-height property.
  // Reads first, then writes ONLY on change — a converged layout performs zero
  // DOM writes here, so the ResizeObserver goes quiet and the system settles.
  // --scrolled is deliberately neither an input nor an output of this method.
  evaluate() {
    if (!this.isPrimary) return; // modalbox etc: single-row nowrap, and it must not write the page-level height property

    const stacked = this.getIsStacked();

    if (stacked !== this.isStacked && !this.oscillationLatched) {
      this.recordFlip();
      if (this.isOscillating()) {
        this.latchStacked();
      } else {
        this.applyStacked(stacked);
      }
    }

    // After any class change above so the measurement reflects the final styling.
    // (The promo lockup needs no JS state of its own any more: it is always
    // visible inline — see _page_heading__action.scss — so the retired
    // --promo_fits measured-reveal state has no successor.)
    this.syncHeightProperty();
  }

  applyStacked(stacked) {
    this.isStacked = stacked;
    this.conditionallySetHtmlClassState(stacked, "stacked");
  }

  recordFlip() {
    const now = Date.now();
    this.flipTimes = this.flipTimes.filter(
      (t) => now - t < OSCILLATION_WINDOW_MS,
    );
    this.flipTimes.push(now);
  }

  isOscillating() {
    return this.flipTimes.length > OSCILLATION_MAX_FLIPS;
  }

  // Backstop, not strategy: latch the state that can never overflow and go quiet
  // until an external trigger (viewport resize, content change, font load) clears
  // the latch. Reaching this line means the --stacked invariant is being violated.
  latchStacked() {
    this.oscillationLatched = true;
    this.applyStacked(true);
    if (!this.hasWarnedOscillation) {
      this.hasWarnedOscillation = true;
      console.warn(
        "[PageHeading] stacking oscillation detected — latched stacked. A " +
          ".page_heading--stacked style is reducing the intrinsic width of " +
          "__start/__end, violating the loop-safety invariant documented in " +
          "app/stylesheets/shared/page_heading/_page_heading.scss.",
        { element: this.element },
      );
    }
  }

  clearOscillationLatch() {
    this.oscillationLatched = false;
    this.flipTimes = [];
  }

  // Publish the heading's actual rendered height for consumers (#page's reserved
  // padding-top). Loop-free by construction: the heading is position:fixed, so
  // changing #page's padding can never change the heading's own geometry — and
  // nothing inside the heading may consume this property (see the SCSS guard).
  syncHeightProperty() {
    const height = this.element.offsetHeight;
    if (!height || height === this.lastSyncedHeight) return;
    this.lastSyncedHeight = height;
    document.documentElement.style.setProperty(
      PageHeading.config.heightProperty,
      `${height}px`,
    );
  }

  render() {
    this.conditionallySetHtmlClassState(this.isScrolled, "scrolled");
    this.conditionallySetHtmlClassState(this.isPrimary, "primary");
    // Non-primary headings (e.g. modalbox content, online_app aux-header content)
    // can inherit --with_left_nav / --with_right_nav from the server response when
    // the same URL is also served as a standalone primary page. Strip them — they
    // only have meaning above the fixed #nav_global / #nav_person at viewport top.
    if (!this.isPrimary) {
      this.removeHtmlClassState("with_left_nav");
      this.removeHtmlClassState("with_right_nav");
    }
  }

  warnIfStructureIsUnexpected() {
    const ALLOWED = ["page_heading__start", "page_heading__end"];
    const offenders = Array.from(this.element.children).filter(
      (c) => !ALLOWED.some((cls) => c.classList.contains(cls))
    );
    if (offenders.length > 0) {
      console.warn(
        "[PageHeading] Unexpected direct children of .page_heading — expected only " +
        ".page_heading__start / .page_heading__end. PageHeading::Renderer enforces this " +
        "structure for ERB callers; other sources (e.g. the native-app Handlebars " +
        "template at app/views/online/apps/api/shared/_page_heading.hbs) must match.",
        { element: this.element, unexpected: offenders }
      );
    }
  }
}
